import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Input, Modal, Button, Radio } from 'src/common';
import styles from '../index.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faLinkSimple } from '@fortawesome/pro-light-svg-icons';
import { IApiWebhook } from 'src/models/company/Webhooks/types';
import { WebhookEventType } from '@tendium/prom-types';
import {
  useUpdateWebhook,
  getUpdateCacheOnUpdateWebhook,
  IUpdateWebhookInput
} from 'src/models/company/Webhooks/hooks';
import { InfoLabel } from 'src/shared';

interface IProps {
  webhook: IApiWebhook;
}

export const WebhookUpdate: FC<IProps> = props => {
  const { webhook } = props;
  const { id, name, url, eventType, secret } = webhook;
  const { t } = useTranslation();
  const [updateWebhook] = useUpdateWebhook();
  const [updateWebhookVisible, setUpdateWebhookVisible] = useState(false);

  const [form] = Form.useForm();

  const initialValues = useMemo((): IUpdateWebhookInput => {
    return {
      name,
      url,
      eventType,
      secret
    };
  }, [eventType, name, secret, url]);

  const onOpen = useCallback(() => {
    setUpdateWebhookVisible(true);
    form.setFieldsValue({ name, url, eventType, secret });
  }, [eventType, form, name, secret, url]);

  const onClose = useCallback(() => {
    form.resetFields();
    setUpdateWebhookVisible(false);
  }, [form]);

  const onUpdateWebhook = useCallback(
    (values: IUpdateWebhookInput) => {
      const { name: nameValue, url: urlValue, eventType: eventTypeValue, secret: secretValue } = values;
      updateWebhook({
        variables: {
          id: id,
          name: nameValue || name,
          url: urlValue || url,
          eventType: eventTypeValue || eventType,
          secret: secretValue || secret
        },
        update: getUpdateCacheOnUpdateWebhook(id),
        optimisticResponse: {
          __typename: 'Mutation',
          updateWebhook: {
            __typename: 'Webhook',
            id,
            name: nameValue || name,
            url: urlValue || url,
            eventType: eventTypeValue || eventType,
            secret: secretValue || secret
          }
        }
      }).catch(() => {
        form.setFields([
          {
            name: 'name',
            errors: [t('Common.unknownErrorDesc')]
          }
        ]);
      });
      onClose();
    },
    [eventType, form, id, name, onClose, secret, updateWebhook, url, t]
  );

  const onKeyPress = useCallback(e => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  }, []);

  return (
    <>
      <Button type={'text'} onClick={onOpen} icon={<FontAwesomeIcon icon={faEdit} />} />
      {updateWebhookVisible && (
        <Modal
          title={<span>{t('Webhooks.updateWebhook')}</span>}
          visible
          onCancel={onClose}
          maskClosable
          width={500}
          footer={null}
          keyboard={false}
          forceRender
        >
          <Form
            initialValues={initialValues}
            onFinish={onUpdateWebhook}
            form={form}
            layout={'vertical'}
            className={styles.form}
            onKeyPress={e => onKeyPress(e)}
          >
            <Form.Item
              name={'name'}
              label={t('Common.name')}
              rules={[{ required: true, message: t('Common.inputRequired', { inputName: t('Common.name') }) }]}
            >
              <Input type={'text'} placeholder={t('Common.name')} />
            </Form.Item>
            <Form.Item
              name={'url'}
              label={<InfoLabel title={t('Webhooks.endpointUrl')} desc={t('Webhooks.endpointUrlDesc')} />}
              rules={[{ required: true, message: t('Common.inputRequired', { inputName: t('Common.url') }) }]}
            >
              <Input
                addonBefore={<FontAwesomeIcon icon={faLinkSimple} />}
                type={'text'}
                placeholder={t('Common.url')}
                autoComplete={'url'}
              />
            </Form.Item>
            <Form.Item
              name={'secret'}
              label={<InfoLabel title={t('Webhooks.secret')} desc={t('Webhooks.secretDesc')} />}
              rules={[{ required: true, message: t('Common.inputRequired', { inputName: t('Webhooks.secret') }) }]}
            >
              <Input.Password
                name={'secret'}
                placeholder={t('Webhooks.secret')}
                type={'text'}
                autoComplete={'one-time-code'}
              />
            </Form.Item>
            <Form.Item
              name={'eventType'}
              label={t('Webhooks.eventType')}
              className={styles.field}
              rules={[{ required: true, message: t('Common.inputRequired', { inputName: t('Webhooks.eventType') }) }]}
            >
              <Radio.Group className={styles.eventTypeCheckboxes}>
                {Object.keys(WebhookEventType).map(eventType => (
                  <Radio value={eventType} key={eventType} className={styles.eventTypeCheckbox}>
                    <span className={styles.eventTypeInner}>
                      <span className={styles.eventTypeName}>{t(`Webhooks.event${eventType}`)}</span>
                      <span className={styles.eventTypeDesc}>{t(`Webhooks.event${eventType}Desc`)}</span>
                    </span>
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>
            <div className={styles.formFooter}>
              <Button type={'text'} onClick={onClose}>
                {t('Common.cancel')}
              </Button>
              <Button type={'primary'} htmlType={'submit'}>
                {t('Common.save')}
              </Button>
            </div>
          </Form>
        </Modal>
      )}
    </>
  );
};

export default WebhookUpdate;
